"use client";

import { RequestInfo } from "rwsdk/worker";
import { AppLayout } from "@/components/layouts";

export function Privacy({ ctx }: RequestInfo) {
  return (
    <AppLayout ctx={ctx}>
      <div className="container mx-auto px-4 py-12 sm:py-16 max-w-3xl">
        <div className="mb-10">
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-2">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Last updated: December 2024</p>
        </div>

        <div className="space-y-6">
          <Section title="Privacy Commitment">
            <p>
              ForwardCents is built with the belief that your financial data is not a product to be sold. This policy explains how we collect, use, and protect your information.
            </p>
          </Section>

          <Section title="Information We Collect">
            <h4>Account Information</h4>
            <ul>
              <li>Your name and email address (via Google OAuth)</li>
              <li>Basic profile information from your Google account</li>
              <li>Your unique ForwardCents email address</li>
            </ul>

            <h4>Expense Data</h4>
            <ul>
              <li>Receipt emails you forward to your ForwardCents address</li>
              <li>Extracted expense information (vendor, amount, date, category)</li>
              <li>PDF attachments from your forwarded emails</li>
            </ul>

            <h4>Usage Information</h4>
            <ul>
              <li>How you interact with our service</li>
              <li>Error logs and technical diagnostics</li>
              <li>Email processing status and timestamps</li>
            </ul>
          </Section>

          <Section title="How Your Information is Used">
            <ul>
              <li><strong>Process your expenses:</strong> Extract data from receipt emails and organize them in your dashboard</li>
              <li><strong>Improve the AI:</strong> Enhance extraction algorithms (using anonymized data only)</li>
              <li><strong>Provide support:</strong> Help you with technical issues and questions</li>
              <li><strong>Secure your account:</strong> Detect and prevent unauthorized access</li>
            </ul>
          </Section>

          <Section title="Privacy by Design">
            <h4>PII Removal</h4>
            <p>
              Before processing emails with AI, we automatically remove personally identifiable information including:
            </p>
            <ul>
              <li>Your name and address</li>
              <li>Credit card numbers</li>
              <li>Phone numbers and other personal identifiers</li>
            </ul>

            <h4>Email Handling</h4>
            <ul>
              <li>Original emails are processed and then permanently deleted</li>
              <li>Only extracted expense data (vendor, amount, date) is stored</li>
              <li>PDF receipts are stored securely for your reference</li>
            </ul>
          </Section>

          <Section title="Data Sharing and Disclosure">
            <p><strong>Your data will never be sold to third parties.</strong></p>
            <p>Your information may be shared only in these limited circumstances:</p>
            <ul>
              <li><strong>With your consent:</strong> When you explicitly authorize sharing</li>
              <li><strong>Legal requirements:</strong> When required by law or to protect rights</li>
              <li><strong>Service providers:</strong> With trusted partners under strict privacy agreements</li>
            </ul>
            <p>Current service providers:</p>
            <ul>
              <li>Cloudflare (hosting and infrastructure)</li>
              <li>Google (authentication)</li>
            </ul>
          </Section>

          <Section title="Data Security">
            <ul>
              <li>All data encrypted in transit and at rest</li>
              <li>User data isolated — you can only access your own expenses</li>
              <li>Regular security audits and monitoring</li>
              <li>Secure cloud infrastructure with Cloudflare Workers</li>
            </ul>
          </Section>

          <Section title="Your Rights">
            <ul>
              <li><strong>Access:</strong> View all data we have about you</li>
              <li><strong>Edit:</strong> Correct any expense information</li>
              <li><strong>Delete:</strong> Permanently delete your account and all data</li>
              <li><strong>Export:</strong> Download your expense data (coming soon)</li>
              <li><strong>Control:</strong> Choose which emails to forward and process</li>
            </ul>
          </Section>

          <Section title="Data Retention">
            <ul>
              <li>Account data: Retained while your account is active</li>
              <li>Expense data: Retained until you delete it or close your account</li>
              <li>Original emails: Processed and immediately deleted</li>
              <li>Deleted data: Permanently removed within 30 days</li>
            </ul>
          </Section>

          <Section title="Changes to This Policy">
            <p>
              This policy may be updated from time to time. You will be notified of material changes by email or through the service. Continued use after changes take effect constitutes acceptance.
            </p>
          </Section>

          <Section title="Contact">
            <p>Questions about this policy or how your data is handled:</p>
            <ul>
              <li>Email: <a href="mailto:hey@harshil.dev" className="underline">hey@harshil.dev</a></li>
              <li>Response time: Within 48 hours</li>
            </ul>
            <p className="mt-4">
              For details on our technical security measures, see our <a href="/security" className="underline hover:text-foreground transition-colors">Security & Privacy</a> page.
            </p>
          </Section>
        </div>
      </div>
    </AppLayout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border rounded-xl p-5">
      <h2 className="text-base font-medium mb-3">{title}</h2>
      <div className="text-sm text-muted-foreground [&_h4]:text-foreground [&_h4]:text-sm [&_h4]:font-medium [&_h4]:mt-4 [&_h4]:mb-2 [&_ul]:space-y-1.5 [&_ul]:text-sm [&_ul]:list-disc [&_ul]:pl-4 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-2.5 [&_strong]:text-foreground">
        {children}
      </div>
    </div>
  );
}
